import { useTranslation } from 'react-i18next';
import { HandMark } from '../HandMark';
import type { BoardHand } from '../../../types/device';

/** The button's ring, which the mark is drawn on — as thick as a circle's. */
const RING_PX = 3;

/**
 * The hand the hold in hand is taken with, and the way to swap it. Present only
 * on a circuit that forces hands; dead while nothing in hand names a hand.
 */
export interface HoldHandControl {
  /**
   * The hand(s) on the hold — both on a match, the one that arrives first — as
   * `holdHands` reads them. Empty where none is named: no hold picked up, a
   * start, a foot.
   */
  hands: BoardHand[];
  /**
   * Take the hold with the other hand, and let the run alternate on from it.
   * On the first real move that is the starting hand being flipped; on any
   * other it is a swap against the alternation — see `circuit.ts`.
   */
  onSwap: () => void;
}

/**
 * The hold's hand as a button: the same marked ring the hold wears on the wall
 * — the arc on the hand's side — with the hand's initials inside, and a press
 * swaps it.
 *
 * The ring rather than an icon so the button and the hold say the same thing
 * in the same way, and the initials because an arc at this size says a side
 * before it says a hand. It reads "SX", it is pressed, it reads "DX", and the
 * numbers after it on the wall change hands with it.
 */
export function HoldHandButton({ control }: { control: HoldHandControl }) {
  const { t } = useTranslation();
  const hand = control.hands[0] ?? null;

  return (
    <button
      type="button"
      onClick={control.onSwap}
      disabled={hand === null}
      aria-label={t('climbEditor.circuit.swapHand')}
      title={
        hand
          ? t('climbEditor.circuit.swapHandFrom', { hand: t(`climbs.board.hands.${hand}`) })
          : t('climbEditor.circuit.swapHand')
      }
      data-testid="hold-hand"
      data-hand={hand ?? undefined}
      className="relative flex size-9 shrink-0 items-center justify-center rounded-full border-[3px] border-border text-[10px] font-bold uppercase transition-transform active:scale-90 disabled:cursor-not-allowed disabled:text-muted-foreground/60"
    >
      <HandMark hands={control.hands} ring={RING_PX} />
      <span>{hand ? t(`climbs.board.handsShort.${hand}`) : '–'}</span>
    </button>
  );
}
